import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { MessageCircle, X, Send, Loader2, Minimize2, Navigation, CheckCircle2, AlertCircle, RotateCcw, Mail } from 'lucide-react';
import VoiceControls from '../shared/VoiceControls';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useDraggable = (initialPos = { x: 0, y: 0 }) => {
  const [pos, setPos] = useState(initialPos);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const wasDragged = useRef(false);

  const onPointerDown = useCallback((e) => {
    dragging.current = true;
    wasDragged.current = false;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    wasDragged.current = true;
    setPos({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  return { pos, handlers: { onPointerDown, onPointerMove, onPointerUp }, wasDragged };
};

const DashboardAIAssistant = ({ onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastAIResponse, setLastAIResponse] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [pendingEmail, setPendingEmail] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { pos, handlers, wasDragged } = useDraggable({ x: 0, y: 0 });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const WRITE_ACTIONS = new Set(['change_status', 'send_message', 'add_note', 'generate_link']);

  const confirmAndSendEmail = async (emailData) => {
    setSendingEmail(true);
    try {
      await axios.post(`${BACKEND_URL}/api/email/ai-send`, {
        to_email: emailData.to,
        subject: emailData.subject,
        body: emailData.body
      });
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Email sent to ${emailData.to}`,
        isSuccess: true
      }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Send failed';
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Email failed: ${detail}`,
        isError: true
      }]);
    } finally {
      setPendingEmail(null);
      setSendingEmail(false);
    }
  };

  const cancelEmail = () => {
    setPendingEmail(null);
    setMessages(prev => [...prev, {
      role: 'system',
      content: 'Email cancelled.',
      isError: false
    }]);
  };

  const executeWriteAction = async (action) => {
    const { type, value } = action;
    const parts = value.split('|').map(p => p.trim());

    try {
      if (type === 'change_status') {
        const [appId, newStatus, reason] = parts;
        const params = new URLSearchParams({ status: newStatus });
        if (reason) params.append('decline_reason', reason);
        await axios.put(`${BACKEND_URL}/api/applications/${appId}/status?${params.toString()}`);
        setMessages(prev => [...prev, {
          role: 'system',
          content: `Status updated successfully.`,
          isSuccess: true
        }]);
      } else if (type === 'send_message') {
        const [appId, channel, ...msgParts] = parts;
        const content = msgParts.join('|');
        await axios.post(`${BACKEND_URL}/api/messages/send`, {
          application_id: appId,
          channel: channel || 'broker_underwriter',
          content
        });
        setMessages(prev => [...prev, {
          role: 'system',
          content: `Message sent successfully.`,
          isSuccess: true
        }]);
      } else if (type === 'add_note') {
        const [appId, ...noteParts] = parts;
        const noteText = noteParts.join('|');
        await axios.post(`${BACKEND_URL}/api/applications/${appId}/notes`, {
          content: noteText
        });
        setMessages(prev => [...prev, {
          role: 'system',
          content: `Note added successfully.`,
          isSuccess: true
        }]);
      } else if (type === 'generate_link') {
        const [appId] = parts;
        const resp = await axios.post(`${BACKEND_URL}/api/borrower/generate-link/${appId}`);
        const link = resp.data?.link || resp.data?.url || 'Link generated';
        setMessages(prev => [...prev, {
          role: 'system',
          content: `Borrower link generated: ${link}`,
          isSuccess: true
        }]);
      }
      // Notify parent to refresh data
      if (onAction) onAction({ type: 'refresh_data' });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Action failed';
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Action failed: ${detail}`,
        isError: true
      }]);
    }
  };

  const executeAction = (action) => {
    if (!action) return;
    if (action.type === 'send_email') {
      const parts = action.value.split('|').map(p => p.trim());
      const [toEmail, subject, ...bodyParts] = parts;
      setPendingEmail({ to: toEmail, subject, body: bodyParts.join('|') });
    } else if (WRITE_ACTIONS.has(action.type)) {
      executeWriteAction(action);
    } else if (onAction) {
      onAction(action);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const conversation = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
      const response = await axios.post(`${BACKEND_URL}/api/dashboard-ai/chat`, {
        message: text,
        conversation
      });
      let aiAction = response.data.action;
      let displayMsg = response.data.message;

      // Frontend fallback: parse action from message text if backend missed it
      if (!aiAction && displayMsg) {
        const patterns = [
          /\[ACTION:(\w+):([^\]]+)\]/,
          /\[ACTION:(\w+)\]/,
          /(?:^|\n|\s)ACTION:(\w+):(.+?)(?:\n|$)/,
          /(?:^|\n|\s)ACTION:(\w+)(?:\n|$)/,
        ];
        for (const pat of patterns) {
          const m = displayMsg.match(pat);
          if (m) {
            aiAction = { type: m[1], value: (m[2] || '').trim() };
            displayMsg = displayMsg.replace(pat, '').trim();
            break;
          }
        }
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: displayMsg,
        meta: `${response.data.applications_count} apps accessible`,
        action: aiAction
      }]);
      setLastAIResponse(displayMsg);

      // Execute any action returned by the AI
      if (aiAction) {
        setTimeout(() => executeAction(aiAction), 600);
      }
    } catch (error) {
      console.error('Dashboard AI error:', error);
      const detail = error?.response?.data?.detail || '';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: detail ? `Sorry, there was an issue: ${detail}` : 'Sorry, I had trouble processing that request. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  const ACTION_LABELS = {
    navigate_tab: 'Navigating',
    open_review: 'Opening review',
    open_edit: 'Opening editor',
    filter_status: 'Filtering',
    filter_loan_type: 'Filtering',
    filter_broker: 'Filtering',
    search: 'Searching',
    clear_filters: 'Clearing filters',
    change_status: 'Updating status',
    send_message: 'Sending message',
    add_note: 'Adding note',
    generate_link: 'Generating link',
    send_email: 'Email ready for review',
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => { if (!wasDragged.current) setIsOpen(true); }}
        {...handlers}
        style={{
          position: 'fixed',
          bottom: `calc(1.5rem - ${pos.y}px)`,
          right: `calc(1.5rem - ${pos.x}px)`,
          touchAction: 'none',
          background: 'linear-gradient(135deg, #66C2CA 0%, #0E333F 100%)',
          boxShadow: '0 8px 32px rgba(14,51,63,0.4), 0 0 0 4px rgba(102,194,202,0.15)'
        }}
        className="z-40 w-12 h-12 rounded-full text-white flex items-center justify-center transition-all hover:scale-110 group cursor-grab active:cursor-grabbing animate-bounce-slow"
        data-testid="dashboard-ai-bubble"
      >
        <MessageCircle className="h-5 w-5 fill-white/20" />
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#5EDEE5] rounded-full border-2 border-white animate-pulse" />
        <span className="absolute -top-10 right-0 bg-[#081120] text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg pointer-events-none font-medium">
          Quidity Research Assistant
        </span>
      </button>
    );
  }

  return (
    <div
      style={{ position: 'fixed', bottom: `calc(1.5rem - ${pos.y}px)`, right: `calc(1.5rem - ${pos.x}px)`, touchAction: 'none' }}
      className="z-50 w-[400px] h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      data-testid="dashboard-ai-panel"
    >
      {/* Header — drag handle */}
      <div
        {...handlers}
        className="flex items-center justify-between px-4 py-3 bg-[#081120] text-white flex-shrink-0 cursor-grab active:cursor-grabbing select-none"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#66C2CA]/30 to-[#5EDEE5]/10 flex items-center justify-center border border-white/10">
            <MessageCircle className="h-4 w-4 text-[#5EDEE5]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Quidity Research Assistant</h3>
            <p className="text-[10px] text-[#66C2CA]">Ask questions or navigate your dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button onPointerDown={(e) => e.stopPropagation()} onClick={clearChat} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-slate-300 hover:text-white" title="Clear chat">
              <Minimize2 className="h-3.5 w-3.5" />
            </button>
          )}
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="close-ai-assistant">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50/50">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#66C2CA]/20 to-[#0E333F]/10 flex items-center justify-center mb-3 border border-[#66C2CA]/20">
              <MessageCircle className="h-6 w-6 text-[#0E333F]" />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>How can I help?</p>
            <p className="text-xs text-slate-500 mb-4">Ask about programs, eligibility, your apps, or where to navigate.</p>
            <div className="space-y-2 w-full">
              {[
                { text: "How many applications are pending?", icon: "?" },
                { text: "What are the guidelines for a Line of Credit?", icon: "?" },
                { text: "Which program fits a business with $2M revenue, 720 FICO, 4 years in business?", icon: "?" },
                { text: "Show me all incomplete apps", icon: "F" },
                { text: "Take me to Messages", icon: "G" },
              ].map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(q.text); }}
                  className="w-full text-left text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-[#66C2CA]/50 hover:bg-[#66C2CA]/5 transition-colors text-slate-600 flex items-center gap-2"
                  data-testid={`ai-suggestion-${i}`}
                >
                  <span className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                    {q.icon === "W" ? <CheckCircle2 className="h-3 w-3 text-slate-400" /> :
                     q.icon === "F" ? <Navigation className="h-3 w-3 text-slate-400" /> :
                     q.icon === "G" ? <MessageCircle className="h-3 w-3 text-slate-400" /> :
                     <span className="text-[10px] text-slate-400 font-bold">?</span>}
                  </span>
                  {q.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => {
          const isUser = msg.role === 'user';
          const isSystem = msg.role === 'system';
          if (isSystem) {
            return (
              <div key={i} className="flex justify-center">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                  msg.isSuccess ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  msg.isError ? 'bg-red-50 text-red-700 border border-red-200' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {msg.isSuccess ? <CheckCircle2 className="h-3 w-3" /> : msg.isError ? <AlertCircle className="h-3 w-3" /> : null}
                  {msg.content}
                </div>
              </div>
            );
          }
          return (
            <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] ${isUser ? '' : 'flex items-start gap-2'}`}>
                {!isUser && (
                  <div className="w-6 h-6 rounded-md bg-[#081120] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircle className="h-3 w-3 text-[#5EDEE5]" />
                  </div>
                )}
                <div>
                  <div className={`px-3 py-2 rounded-xl text-sm leading-relaxed ${
                    isUser
                      ? 'bg-[#081120] text-white rounded-br-sm'
                      : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.action && (
                    <div className="flex items-center gap-1.5 mt-1 ml-1">
                      <Navigation className="h-3 w-3 text-[#66C2CA]" />
                      <span className="text-[10px] text-[#66C2CA] font-medium">
                        {ACTION_LABELS[msg.action.type] || 'Action performed'}
                      </span>
                    </div>
                  )}
                  {msg.meta && !msg.action && (
                    <p className="text-[10px] text-slate-400 mt-0.5 ml-1">{msg.meta}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {pendingEmail && (
          <div className="mx-1" data-testid="email-confirmation-card">
            <div className="bg-white border border-[#66C2CA]/40 rounded-xl shadow-md overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-[#081120]">
                <Mail className="h-3.5 w-3.5 text-[#5EDEE5]" />
                <span className="text-xs font-semibold text-white">Review Email Before Sending</span>
              </div>
              <div className="px-3 py-2.5 space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide w-10 flex-shrink-0">To</span>
                  <span className="text-xs text-slate-700 font-medium">{pendingEmail.to}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide w-10 flex-shrink-0">Subj</span>
                  <span className="text-xs text-slate-700 font-medium">{pendingEmail.subject}</span>
                </div>
                <div className="border-t border-slate-100 pt-2">
                  <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed max-h-[120px] overflow-y-auto">{pendingEmail.body}</p>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => confirmAndSendEmail(pendingEmail)}
                    disabled={sendingEmail}
                    className="flex-1 flex items-center justify-center gap-1.5 h-8 bg-[#081120] text-white text-xs font-semibold rounded-lg hover:bg-[#0E333F] transition-colors disabled:opacity-50"
                    data-testid="email-confirm-send"
                  >
                    {sendingEmail ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                  <button
                    onClick={cancelEmail}
                    disabled={sendingEmail}
                    className="flex-1 h-8 text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                    data-testid="email-confirm-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex justify-start">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-md bg-[#081120] flex items-center justify-center flex-shrink-0">
                <MessageCircle className="h-3 w-3 text-[#5EDEE5]" />
              </div>
              <div className="px-3 py-2.5 rounded-xl rounded-bl-sm bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                  <span className="text-xs text-slate-500">Researching...</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 border-t bg-white flex-shrink-0">
        {voiceError && (
          <p className="text-xs text-red-500 mb-1.5 px-1 bg-red-50 p-2 rounded-md border border-red-100">{voiceError}</p>
        )}
        <form onSubmit={sendMessage} className="flex items-center gap-1.5">
          <VoiceControls
            compact
            onTranscription={(text) => setInput(prev => prev ? prev + ' ' + text : text)}
            onError={(msg) => { setVoiceError(msg); setTimeout(() => setVoiceError(''), 5000); }}
            textToSpeak={lastAIResponse}
          />
          {messages.length > 0 && (
            <button
              type="button"
              onClick={clearChat}
              className="h-9 px-2 flex items-center gap-1 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors flex-shrink-0"
              title="New conversation"
              data-testid="ai-new-chat-btn"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span className="text-[11px] font-medium">New</span>
            </button>
          )}
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question or navigate..."
            disabled={loading}
            className="flex-1 h-9 px-3 text-sm text-slate-900 placeholder-slate-400 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#66C2CA]/40 focus:border-transparent bg-white"
            data-testid="dashboard-ai-input"
          />
          <Button
            type="submit"
            disabled={!input.trim() || loading}
            className="bg-[#081120] hover:bg-[#0E333F] h-9 w-9 p-0 flex-shrink-0"
            data-testid="dashboard-ai-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default DashboardAIAssistant;
